/**
 * PayPay QR決済コンポーネント
 * QRコード生成と決済状態のポーリング
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Paper,
} from '@mui/material';
import {
  QrCode as QrCodeIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import api from '../../services/api';

// 決済状態
const PAYMENT_STATUS = {
  IDLE: 'idle',
  GENERATING: 'generating',
  WAITING: 'waiting',
  POLLING: 'polling',
  SUCCESS: 'success',
  EXPIRED: 'expired',
  ERROR: 'error',
};

// ポーリング設定
const POLLING_INTERVAL = 3000; // 3秒
const MAX_POLLING_TIME = 300000; // 5分

const PayPayPayment = ({
  amount,
  orderId,
  storeId,
  onSuccess,
  onError,
  onCancel,
  disabled = false,
}) => {
  const [status, setStatus] = useState(PAYMENT_STATUS.IDLE);
  const [, setQrCodeUrl] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
  const [, setMerchantPaymentId] = useState(null);
  const [error, setError] = useState(null);
  const [, setExpiresAt] = useState(null);
  const [remainingTime, setRemainingTime] = useState(null);

  const pollingRef = useRef(null);
  const countdownRef = useRef(null);
  const startTimeRef = useRef(null);

  // クリーンアップ
  const cleanup = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // コンポーネントアンマウント時にクリーンアップ
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // QRコード生成
  const generateQRCode = useCallback(async () => {
    setStatus(PAYMENT_STATUS.GENERATING);
    setError(null);

    try {
      const response = await api.post('/payments/paypay/create', {
        orderId,
        storeId,
        amount,
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'QRコード生成に失敗しました');
      }

      const { qrCode } = response.data;
      setQrCodeUrl(qrCode.url);
      setMerchantPaymentId(qrCode.merchantPaymentId);
      setExpiresAt(qrCode.expiresAt ? new Date(qrCode.expiresAt) : null);

      // QRコードをData URLに変換
      const dataUrl = await QRCode.toDataURL(qrCode.url, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      });
      setQrCodeDataUrl(dataUrl);

      setStatus(PAYMENT_STATUS.WAITING);
      startTimeRef.current = Date.now();

      // ポーリング開始
      startPolling(qrCode.merchantPaymentId);

      // カウントダウン開始
      if (qrCode.expiresAt) {
        startCountdown(new Date(qrCode.expiresAt));
      }
    } catch (err) {
      console.error('PayPay QRコード生成エラー:', err);
      setError(err.response?.data?.message || err.message);
      setStatus(PAYMENT_STATUS.ERROR);
      onError?.(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, storeId, amount, onError]);

  // 決済状態のポーリング
  const startPolling = useCallback((paymentId) => {
    pollingRef.current = setInterval(async () => {
      // 最大ポーリング時間チェック
      if (Date.now() - startTimeRef.current > MAX_POLLING_TIME) {
        cleanup();
        setStatus(PAYMENT_STATUS.EXPIRED);
        setError('決済がタイムアウトしました');
        return;
      }

      try {
        setStatus(PAYMENT_STATUS.POLLING);

        const response = await api.get(`/payments/paypay/status/${paymentId}`);

        if (response.data.success) {
          if (response.data.status === 'COMPLETED') {
            cleanup();
            setStatus(PAYMENT_STATUS.SUCCESS);
            onSuccess?.({
              paymentId: response.data.paymentId,
              merchantPaymentId: paymentId,
              paidAt: response.data.paidAt,
            });
          } else if (response.data.status === 'EXPIRED' || response.data.status === 'CANCELED') {
            cleanup();
            setStatus(PAYMENT_STATUS.EXPIRED);
            setError('決済がキャンセルまたは期限切れになりました');
          }
          // それ以外（PENDING等）は継続してポーリング
        }
      } catch (err) {
        console.error('PayPay状態確認エラー:', err);
        // ネットワークエラーの場合は継続
      }
    }, POLLING_INTERVAL);
  }, [cleanup, onSuccess]);

  // カウントダウン
  const startCountdown = useCallback((expiry) => {
    countdownRef.current = setInterval(() => {
      const now = new Date();
      const remaining = Math.max(0, Math.floor((expiry - now) / 1000));

      if (remaining <= 0) {
        cleanup();
        setStatus(PAYMENT_STATUS.EXPIRED);
        setError('QRコードの有効期限が切れました');
        setRemainingTime(null);
      } else {
        setRemainingTime(remaining);
      }
    }, 1000);
  }, [cleanup]);

  // 残り時間のフォーマット
  const formatRemainingTime = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // リセット
  const handleReset = () => {
    cleanup();
    setStatus(PAYMENT_STATUS.IDLE);
    setQrCodeUrl(null);
    setQrCodeDataUrl(null);
    setMerchantPaymentId(null);
    setError(null);
    setExpiresAt(null);
    setRemainingTime(null);
  };

  // 決済成功表示
  if (status === PAYMENT_STATUS.SUCCESS) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" color="success.main" gutterBottom>
          PayPay決済完了
        </Typography>
        <Typography variant="body2" color="text.secondary">
          決済が正常に処理されました
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* エラー表示 */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleReset}>
              再試行
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* 初期状態 - QRコード生成ボタン */}
      {status === PAYMENT_STATUS.IDLE && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<QrCodeIcon />}
            onClick={generateQRCode}
            disabled={disabled}
            sx={{
              bgcolor: '#FF0033',
              '&:hover': { bgcolor: '#CC0029' },
            }}
          >
            PayPay QRコードを表示
          </Button>
          <Box sx={{ mt: 2 }}>
            <Button variant="text" onClick={onCancel}>
              キャンセル
            </Button>
          </Box>
        </Box>
      )}

      {/* QRコード生成中 */}
      {status === PAYMENT_STATUS.GENERATING && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress sx={{ mr: 2 }} />
          <Typography>QRコードを生成中...</Typography>
        </Box>
      )}

      {/* QRコード表示 - 決済待機中 */}
      {(status === PAYMENT_STATUS.WAITING || status === PAYMENT_STATUS.POLLING) && qrCodeDataUrl && (
        <Box sx={{ textAlign: 'center' }}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              display: 'inline-block',
              bgcolor: '#FFFFFF',
            }}
          >
            <img
              src={qrCodeDataUrl}
              alt="PayPay QRコード"
              style={{ display: 'block', width: 256, height: 256 }}
            />
          </Paper>

          <Box sx={{ mt: 2 }}>
            <Typography variant="h4" color="error.main" fontWeight="bold">
              ¥{amount?.toLocaleString()}
            </Typography>

            {remainingTime !== null && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                有効期限: {formatRemainingTime(remainingTime)}
              </Typography>
            )}

            <Alert
              severity="info"
              sx={{ mt: 2, textAlign: 'left' }}
              icon={status === PAYMENT_STATUS.POLLING ? <CircularProgress size={20} /> : undefined}
            >
              <Typography variant="body2">
                1. PayPayアプリでQRコードをスキャン
              </Typography>
              <Typography variant="body2">
                2. アプリ内で支払いを確定
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                {status === PAYMENT_STATUS.POLLING ? '決済確認中...' : '支払いを待っています...'}
              </Typography>
            </Alert>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
            >
              QRコードを再生成
            </Button>
            <Button
              variant="text"
              startIcon={<CancelIcon />}
              onClick={() => {
                cleanup();
                onCancel?.();
              }}
            >
              キャンセル
            </Button>
          </Box>
        </Box>
      )}

      {/* 期限切れ */}
      {status === PAYMENT_STATUS.EXPIRED && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            QRコードの有効期限が切れました
          </Alert>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => {
              handleReset();
              generateQRCode();
            }}
          >
            新しいQRコードを生成
          </Button>
          <Box sx={{ mt: 1 }}>
            <Button variant="text" onClick={onCancel}>
              他の決済方法を選択
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PayPayPayment;
